import { useState, useEffect } from 'react'
import { Box, Typography, CircularProgress, Chip, Paper } from '@mui/material'
import { aiInsights } from '../../../../services/analytics/analytics.service'

// MF-506 — Inteligencia de demanda (Maestro-only). Muestra los agregados del ConversationLog
// del tenant: uso, % de éxito, tipos de chart y las preguntas más frecuentes → qué construir.
const Stat = ({ label, value }) => (
  <Paper variant='outlined' sx={{ p: 1.5, minWidth: 120, flex: 1 }}>
    <Typography variant='h5' color='primary'>
      {value}
    </Typography>
    <Typography variant='caption' color='textSecondary'>
      {label}
    </Typography>
  </Paper>
)

const AiInsights = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    aiInsights()
      .then((res) => {
        if (alive) setData(res?.data || null)
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} /> <Typography variant='body2'>Loading insights…</Typography>
      </Box>
    )
  }

  if (!data || !data.total) {
    return (
      <Typography variant='body2' color='textSecondary' sx={{ py: 2 }}>
        No usage yet. As people ask questions in the chat, demand insights will build up here.
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Stat label='Questions asked' value={data.total} />
        <Stat label='Answered with a chart' value={`${data.successRate}%`} />
        <Stat label='Tokens used (in/out)' value={`${data.tokens.input}/${data.tokens.output}`} />
      </Box>

      {data.byChartType?.length > 0 && (
        <Box>
          <Typography variant='subtitle2' gutterBottom>
            Chart types generated
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.byChartType.map((c) => (
              <Chip key={c.type} label={`${c.type}: ${c.n}`} size='small' />
            ))}
          </Box>
        </Box>
      )}

      {data.byType?.length > 0 && (
        <Box>
          <Typography variant='subtitle2' gutterBottom>
            Outcomes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {data.byType.map((c) => (
              <Chip key={c.type} label={`${c.type}: ${c.n}`} size='small' variant='outlined' />
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Typography variant='subtitle2' gutterBottom>
          Most asked questions — candidates for standard charts
        </Typography>
        {data.topQuestions.map((q, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              py: 0.75,
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {q.question}
            </Typography>
            <Typography variant='caption' color='textSecondary' sx={{ flexShrink: 0 }}>
              {q.n}× · {q.ok} ok
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default AiInsights
